// janetAnswerFormat — pure, safe-readable-formatting parser for Janet's chat
// answers (issue #254 finding 4: "complete the original formatting/
// source-link acceptance requirement without arbitrary HTML, unsafe links,
// or raw protocol/citation scaffolding"). Turns plain text containing a
// small, well-known markdown-ish subset (paragraphs, bullet/numbered lists,
// **bold**, `inline code`, and fenced ```code``` blocks) into a tree of
// PLAIN DATA blocks/spans — never HTML, never a `dangerouslySetInnerHTML`
// string. The renderer (JanetChatPanel.jsx) turns these into ordinary React
// elements, so every character of the model's own text still passes through
// React's normal text-node escaping; nothing here can ever turn a literal
// `<script>` or a quoted citation/protocol fragment into live markup.
//
// Deliberately NOT supported: italics (a single `*` collides with bullet
// markers far too often to parse unambiguously and safely), raw links/
// autolinking (a URL embedded in prose is shown as plain text — the
// separate, independently-validated citation chips in AnswerSources are the
// only clickable destinations this surface ever renders), and any HTML tag
// interpretation at all.

const FENCE_RE = /```([\s\S]*?)```/g
const BULLET_RE = /^[-*]\s+/
const NUMBERED_RE = /^\d+[.)]\s+/

// Splits one line's text into inline spans: code spans first (so a `**` run
// inside backticks is never mistaken for bold), then bold runs within the
// remaining plain segments. Any unmatched/trailing marker (`**` with no
// close, a lone backtick) is left as literal text — never silently dropped.
export function tokenizeInline(line) {
  const spans = []
  const codeParts = String(line || '').split(/(`[^`]+`)/)
  for (const part of codeParts) {
    if (!part) continue
    if (part.length >= 2 && part.startsWith('`') && part.endsWith('`')) {
      spans.push({ type: 'code', text: part.slice(1, -1) })
      continue
    }
    const boldParts = part.split(/(\*\*[^*]+\*\*)/)
    for (const bp of boldParts) {
      if (!bp) continue
      if (bp.length >= 4 && bp.startsWith('**') && bp.endsWith('**')) {
        spans.push({ type: 'bold', text: bp.slice(2, -2) })
      } else {
        spans.push({ type: 'text', text: bp })
      }
    }
  }
  return spans
}

function parseProseBlocks(segment) {
  const blocks = []
  const paragraphs = String(segment || '').split(/\n{2,}/)
  for (const para of paragraphs) {
    const trimmed = para.replace(/^\n+|\n+$/g, '')
    if (!trimmed.trim()) continue
    const lines = trimmed.split('\n').filter(l => l.length > 0)
    if (lines.length && lines.every(l => BULLET_RE.test(l))) {
      blocks.push({ type: 'ul', items: lines.map(l => tokenizeInline(l.replace(BULLET_RE, ''))) })
    } else if (lines.length && lines.every(l => NUMBERED_RE.test(l))) {
      blocks.push({ type: 'ol', items: lines.map(l => tokenizeInline(l.replace(NUMBERED_RE, ''))) })
    } else {
      blocks.push({ type: 'p', lines: lines.map(l => tokenizeInline(l)) })
    }
  }
  return blocks
}

// Turns `text` into an ordered array of block descriptors:
//   { type: 'p',  lines: [spans, ...] }   — one or more line's worth of spans
//   { type: 'ul', items: [spans, ...] }   — one spans-array per bullet item
//   { type: 'ol', items: [spans, ...] }   — one spans-array per numbered item
//   { type: 'pre', text: '...' }          — a fenced code block, VERBATIM —
//                                            no inline parsing at all, so
//                                            quoted code/XML/protocol text
//                                            inside a fence is never touched.
// Always returns an array (possibly empty for blank input) — never throws,
// never returns a bare string a caller could mistake for pre-escaped HTML.
export function parseAnswerBlocks(raw) {
  const text = String(raw || '')
  const blocks = []
  let lastIndex = 0
  let m
  FENCE_RE.lastIndex = 0
  while ((m = FENCE_RE.exec(text))) {
    if (m.index > lastIndex) blocks.push(...parseProseBlocks(text.slice(lastIndex, m.index)))
    // An optional leading language tag line (```json\n...) is stripped —
    // it's a fence annotation, not part of the quoted content — along with
    // the single trailing newline that separates the last content line from
    // the closing ``` delimiter.
    const body = m[1].replace(/^[A-Za-z0-9_-]*\n/, '').replace(/\n$/, '')
    blocks.push({ type: 'pre', text: body })
    lastIndex = FENCE_RE.lastIndex
  }
  if (lastIndex < text.length) blocks.push(...parseProseBlocks(text.slice(lastIndex)))
  return blocks
}
